import DownloadIcon from "@mui/icons-material/Download";
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import useFetchCompany from "../../../data/hooks/useFetchCompany";
import { getAllMyReports } from "../../../services/requests/ApiRequests";
import { CONSTANT_PAYMENT_METHODS } from "../../constants";

const GestionarReports = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const registerId = searchParams.get("rid");
    const dateFrom = searchParams.get("date_from");
    const dateTo = searchParams.get("date_to");
    const fromReportId = searchParams.get("from_report_id");
    const toReportId = searchParams.get("to_report_id");
    const [reports, setReports] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uniqueTaxes, setUniqueTaxes] = useState([]);
    const [usedPaymentMethods, setUsedPaymentMethods] = useState([]);
    const [totals, setTotals] = useState({
        receipts: 0,
    });
    const [generatedTime, setGeneratedTime] = useState(new Date());
    const { company, loading: isCompoanyLoading } = useFetchCompany({
        skip: 0,
        limit: 20,
    });

    const printRef = useRef(null);

    const handleDownloadExcel = () => {
        if (!reports || reports.length === 0) return;

        const data = [];

        // Header row
        const headerRow = [
            "Numărul Raportului Z",
            "Data",
            "Ora",
            "Numărul bonurilor fiscale",
        ];

        // Add tax columns to header
        uniqueTaxes.forEach((tax) => {
            const labels = getTaxColumnLabels(tax);
            headerRow.push(labels.total);
            headerRow.push(labels.tva);
        });

        // Add payment method columns to header
        usedPaymentMethods.forEach((method) => {
            headerRow.push(method.printName);
        });

        data.push(headerRow);

        // Data rows
        reports.forEach((report) => {
            if (!report || !report.soap_data) return;

            const datetime = report.soap_data.datetime
                ? report.soap_data.datetime.split("T")
                : ["", ""];

            const row = [
                report.soap_data.report || "-",
                datetime[0] || "",
                datetime[1] || "",
                report.soap_data.daily?.receipts?.field?.[0]?.count || "0",
            ];

            // Add tax values
            uniqueTaxes.forEach((tax) => {
                const values = getTaxValues(report, tax);
                row.push(parseFloat(values.total) || 0);
                row.push(parseFloat(values.tva) || 0);
            });

            // Add payment values
            usedPaymentMethods.forEach((method) => {
                const paymentValue = getPaymentValue(report, method.type);
                row.push(parseFloat(paymentValue) || 0);
            });

            data.push(row);
        });

        // Totals row
        const totalsRow = ["Total", "", "", totals?.receipts || 0];

        uniqueTaxes.forEach((tax) => {
            const key = `${tax.code}-${tax.percent}`;
            totalsRow.push(parseFloat(totals?.[`${key}-total`]) || 0);
            totalsRow.push(parseFloat(totals?.[`${key}-tva`]) || 0);
        });

        usedPaymentMethods.forEach((method) => {
            const totalKey = `payment-${method.type}`;
            totalsRow.push(parseFloat(totals?.[totalKey]) || 0);
        });

        data.push(totalsRow);

        // Create workbook and worksheet
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Gestionar Reports");

        // Set column widths
        const colWidths = [{ wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 15 }];
        uniqueTaxes.forEach(() => {
            colWidths.push({ wch: 15 });
            colWidths.push({ wch: 15 });
        });
        usedPaymentMethods.forEach(() => {
            colWidths.push({ wch: 15 });
        });
        ws["!cols"] = colWidths;

        // Generate filename
        const filename = `Gestionar_Reports_${dateFrom}_${dateTo}.xlsx`;

        // Download file
        XLSX.writeFile(wb, filename);
    };

    const getPaymentMethodBgColor = (methodType) => {
        if (!methodType) return undefined;

        if (methodType.startsWith("8")) return "rgba(25, 118, 210, 0.05)";
        if (methodType.startsWith("3")) return "rgba(76, 175, 80, 0.05)";
        if (methodType === "1") return "rgba(255, 235, 238, 0.2)";
        if (methodType === "2") return "rgba(232, 245, 233, 0.2)";

        return undefined;
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || isNaN(parseFloat(value))) {
            return "0,00";
        }

        try {
            return new Intl.NumberFormat("ro-MD", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(parseFloat(value));
        } catch (e) {
            console.error("Error formatting currency:", e);
            return "0,00";
        }
    };

    const calculateTotals = (reports, taxes, paymentMethods) => {
        if (!reports || !Array.isArray(reports) || reports.length === 0) {
            const emptyResult = {
                receipts: 0,
            };

            if (taxes && Array.isArray(taxes)) {
                taxes.forEach((tax) => {
                    const key = `${tax.code}-${tax.percent}`;
                    emptyResult[`${key}-total`] = "0.00";
                    emptyResult[`${key}-tva`] = "0.00";
                });
            }

            if (paymentMethods && Array.isArray(paymentMethods)) {
                paymentMethods.forEach((method) => {
                    emptyResult[`payment-${method.type}`] = "0.00";
                });
            }

            return emptyResult;
        }

        const result = {
            receipts: 0,
        };

        if (taxes && Array.isArray(taxes)) {
            taxes.forEach((tax) => {
                const key = `${tax.code}-${tax.percent}`;
                result[`${key}-total`] = 0;
                result[`${key}-tva`] = 0;
            });
        }

        if (paymentMethods && Array.isArray(paymentMethods)) {
            paymentMethods.forEach((method) => {
                result[`payment-${method.type}`] = 0;
            });
        }

        reports.forEach((report) => {
            if (!report || !report.soap_data || !report.soap_data.daily) {
                return;
            }

            try {
                const receiptsCount =
                    report.soap_data.daily.receipts?.field[0]?.count;
                result.receipts += parseInt(receiptsCount || "0", 10) || 0;
            } catch (e) {
                console.error("Error processing receipts:", e);
            }

            try {
                if (report.soap_data.daily?.payments?.field) {
                    const paymentFields = Array.isArray(
                        report.soap_data.daily.payments.field
                    )
                        ? report.soap_data.daily.payments.field
                        : [report.soap_data.daily.payments.field];

                    paymentFields.forEach((payment) => {
                        if (payment && payment.type != null) {
                            const paymentKey = `payment-${String(payment.type).trim()}`;
                            if (result[paymentKey] === undefined) {
                                result[paymentKey] = 0;
                            }
                            result[paymentKey] +=
                                parseFloat(payment.total || "0") || 0;
                        }
                    });
                }
            } catch (e) {
                console.error("Error processing payments:", e);
            }

            try {
                const taxFields = report.soap_data.daily.taxes?.field;
                if (taxFields) {
                    if (Array.isArray(taxFields)) {
                        taxFields.forEach((tax) => {
                            if (tax && tax.code && tax.percent) {
                                const key = `${tax.code}-${tax.percent}`;
                                if (result[`${key}-total`] !== undefined) {
                                    result[`${key}-total`] +=
                                        parseFloat(tax.cost || "0") || 0;
                                    result[`${key}-tva`] +=
                                        parseFloat(tax.rate || "0") || 0;
                                }
                            }
                        });
                    } else if (taxFields.code && taxFields.percent) {
                        const tax = taxFields;
                        const key = `${tax.code}-${tax.percent}`;
                        if (result[`${key}-total`] !== undefined) {
                            result[`${key}-total`] +=
                                parseFloat(tax.cost || "0") || 0;
                            result[`${key}-tva`] +=
                                parseFloat(tax.rate || "0") || 0;
                        }
                    }
                }
            } catch (e) {
                console.error("Error processing tax fields:", e);
            }
        });

        Object.keys(result).forEach((key) => {
            if (key !== "receipts") {
                result[key] = result[key].toFixed(2);
            }
        });

        return result;
    };

    const getTaxColumnLabels = (tax) => {
        if (!tax || !tax.code) {
            return {
                total: "Total",
                tva: "TVA",
            };
        }

        if (tax.code === "_") {
            return {
                total: "Total fără TVA",
                tva: "TVA fără TVA",
            };
        }
        return {
            total: `Total ${tax.code} ${tax.percent}%`,
            tva: `TVA ${tax.code} ${tax.percent}%`,
        };
    };

    const getTaxValues = (report, tax) => {
        if (
            !report ||
            !report.soap_data ||
            !report.soap_data.daily ||
            !report.soap_data.daily.taxes ||
            !tax
        ) {
            return {
                total: "0",
                tva: "0",
            };
        }

        const taxFields = report.soap_data.daily.taxes.field;
        let taxItem = null;

        if (Array.isArray(taxFields)) {
            taxItem = taxFields.find(
                (t) => t && t.code === tax.code && t.percent === tax.percent
            );
        } else if (
            taxFields &&
            taxFields.code === tax.code &&
            taxFields.percent === tax.percent
        ) {
            taxItem = taxFields;
        }

        return {
            total: taxItem ? taxItem.cost : "0",
            tva: taxItem ? taxItem.rate : "0",
        };
    };

    const getPaymentValue = (report, paymentType) => {
        if (
            !report ||
            !report.soap_data ||
            !report.soap_data.daily ||
            !report.soap_data.daily.payments
        ) {
            return "0";
        }

        try {
            const paymentFields = Array.isArray(
                report.soap_data.daily.payments.field
            )
                ? report.soap_data.daily.payments.field
                : [report.soap_data.daily.payments.field];

            const payment = paymentFields.find(
                (p) => p && p.type === paymentType
            );
            return payment ? payment.total : "0";
        } catch (e) {
            console.error(
                `Error getting payment value for type ${paymentType}:`,
                e
            );
            return "0";
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("ro-MD", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            });
        } catch (e) {
            console.error("Error formatting date:", e);
            return dateString || "";
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return "";
        try {
            const time = new Date(`2000-01-01T${timeString}`);
            return time.toLocaleTimeString("ro-MD", {
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch (e) {
            console.error("Error formatting time:", e);
            return timeString || "";
        }
    };

    const formatDateTime = (date) => {
        try {
            return date.toLocaleString("ro-MD", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            });
        } catch (e) {
            console.error("Error formatting datetime:", e);
            return "";
        }
    };

    const getReports = () => {
        if (!company || !registerId) return;

        try {
            const selectedCash = company.cash_registers.find(
                (cash) => cash._id === Number.parseInt(registerId)
            );

            if (!selectedCash) {
                console.error("Cash register not found");
                setLoading(false);
                return;
            }

            const params = {
                report_type: "1",
                limit: 10000,
                start: 0,
                date_from: decodeURIComponent(dateFrom),
                date_to: decodeURIComponent(dateTo),
                idnx: company.idno,
                address: selectedCash.address,
                factory: selectedCash.factory,
                key: selectedCash.key,
                model: selectedCash.model,
                name: company.name,
                number: selectedCash.number,
                point: selectedCash.point,
                type: 3,
                ...(fromReportId && { id_from: fromReportId }),
                ...(toReportId && { id_to: toReportId }),
            };

            getAllMyReports(params)
                .then((data) => {
                    if (!data || !data.data || !data.data.data) {
                        throw new Error("Invalid API response format");
                    }

                    const reportsData = data.data.data || [];
                    setReports(reportsData);

                    const taxes = new Map();

                    const usedPaymentTypes = new Set();

                    if (Array.isArray(reportsData)) {
                        reportsData.forEach((report) => {
                            if (report?.soap_data?.daily?.taxes?.field) {
                                const taxFields =
                                    report.soap_data.daily.taxes.field;
                                if (Array.isArray(taxFields)) {
                                    taxFields.forEach((tax) => {
                                        if (tax && tax.code && tax.percent) {
                                            const key = `${tax.code}-${tax.percent}`;
                                            taxes.set(key, {
                                                code: tax.code,
                                                percent: tax.percent,
                                            });
                                        }
                                    });
                                } else if (
                                    taxFields &&
                                    taxFields.code &&
                                    taxFields.percent
                                ) {
                                    const tax = taxFields;
                                    const key = `${tax.code}-${tax.percent}`;
                                    taxes.set(key, {
                                        code: tax.code,
                                        percent: tax.percent,
                                    });
                                }
                            }

                            if (report?.soap_data?.daily?.payments?.field) {
                                let paymentFields =
                                    report.soap_data.daily.payments.field;

                                if (!Array.isArray(paymentFields)) {
                                    paymentFields = [paymentFields];
                                }

                                paymentFields.forEach((payment) => {
                                    if (payment && payment.type != null) {
                                        usedPaymentTypes.add(String(payment.type).trim());
                                    }
                                });
                            }
                        });
                    }

                    const uniqueTaxList = Array.from(taxes.values());
                    setUniqueTaxes(uniqueTaxList);

                    const methods = [];
                    usedPaymentTypes.forEach((paymentType) => {
                        const knownMethod = CONSTANT_PAYMENT_METHODS.find(m => m.type === paymentType);
                        if (knownMethod) {
                            methods.push(knownMethod);
                        } else {
                            methods.push({
                                type: paymentType,
                                methodName: `payment_type_${paymentType}`,
                                printName: `Tip ${paymentType}`,
                                limitToTotalPrice: true,
                            });
                        }
                    });

                    methods.sort((a, b) => a.type.localeCompare(b.type));

                    setUsedPaymentMethods(methods);

                    const reportTotals = calculateTotals(
                        reportsData,
                        uniqueTaxList,
                        methods
                    );
                    setTotals(reportTotals);

                    setGeneratedTime(new Date());
                })
                .catch((error) => {
                    console.error("Error fetching reports:", error);
                    setReports([]);
                    setUniqueTaxes([]);
                    setUsedPaymentMethods([]);
                    setTotals(calculateTotals([], [], []));
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            console.error("Error in getReports:", error);
            setLoading(false);
            setReports([]);
            setTotals(calculateTotals([], [], []));
        }
    };

    const renderPaymentMethodColumns = () => {
        return usedPaymentMethods.map((method) => (
            <TableCell
                key={`payment-${method.type}`}
                align="center"
                sx={{
                    fontWeight: "bold",
                    minWidth: 120,
                    borderBottom: "1px solid rgba(224, 224, 224, 1)",
                    fontSize: "12px",
                    bgcolor: "white",
                }}
            >
                <Tooltip
                    title={t(method.methodName) || method.methodName}
                    arrow
                >
                    <span>{method.printName}</span>
                </Tooltip>
            </TableCell>
        ));
    };

    const renderPaymentValueCells = (report) => {
        return usedPaymentMethods.map((method) => {
            const paymentValue = getPaymentValue(report, method.type);
            const formattedValue = formatCurrency(paymentValue);
            const hasValue = parseFloat(paymentValue) > 0;

            return (
                <TableCell
                    key={`payment-value-${method.type}`}
                    align="center"
                    sx={{
                        padding: "6px 16px",
                        borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                        fontWeight: hasValue ? 500 : 400,
                        color: hasValue ? "text.primary" : "text.secondary",
                        bgcolor: getPaymentMethodBgColor(method.type),
                    }}
                >
                    {formattedValue}
                </TableCell>
            );
        });
    };

    const renderPaymentTotalCells = () => {
        return usedPaymentMethods.map((method) => {
            const totalKey = `payment-${method.type}`;
            const total = totals?.[totalKey] || "0";
            const hasValue = parseFloat(total) > 0;

            return (
                <TableCell
                    key={`payment-total-${method.type}`}
                    align="center"
                    sx={{
                        padding: "8px 16px",
                        fontWeight: hasValue ? 600 : 500,
                        bgcolor: getPaymentMethodBgColor(method.type),
                    }}
                >
                    {formatCurrency(total)}
                </TableCell>
            );
        });
    };

    useEffect(() => {
        if (!company) return;
        getReports();
    }, [company]);

    if ((isCompoanyLoading && !company) || (loading && !reports)) {
        return (
            <Box
                sx={{
                    flexGrow: 1,
                    marginTop: 3,
                    marginRight: 3,
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (reports && reports.length === 0) {
        return (
            <Box
                sx={{
                    flexGrow: 1,
                    marginTop: 3,
                    marginRight: 3,
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                }}
            >
                <Paper
                    elevation={1}
                    sx={{
                        padding: 3,
                        borderRadius: 1,
                        textAlign: "center",
                    }}
                >
                    Nu există rapoarte pentru perioada selectată
                </Paper>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                flexGrow: 1,
                marginTop: 3,
                marginRight: 3,
            }}
        >
            <Paper
                elevation={1}
                sx={{
                    width: "100%",
                    borderRadius: 1,
                    padding: "20px",
                    marginBottom: "16px",
                    background: "linear-gradient(to right, #f8f9fa, #ffffff)",
                    borderLeft: "4px solid #1976d2",
                    boxSizing: "border-box",
                }}
            >
                <Typography
                    variant="h6"
                    component="h1"
                    fontWeight="bold"
                    sx={{
                        fontSize: "18px",
                        marginBottom: "16px",
                        color: "#1976d2",
                    }}
                >
                    Informație gestionară
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 500,
                                width: "150px",
                                color: "#555",
                                fontSize: "14px",
                            }}
                        >
                            Casa de marcat:
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 400,
                                fontSize: "14px",
                            }}
                        >
                            {company?.cash_registers.find(
                                (cash) =>
                                    cash._id === Number.parseInt(registerId)
                            )?.number || "-"}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 500,
                                width: "150px",
                                color: "#555",
                                fontSize: "14px",
                            }}
                        >
                            Perioada:
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 400,
                                fontSize: "14px",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    padding: "4px 8px",
                                    background: "#f0f7ff",
                                    borderRadius: "4px",
                                    color: "#0066cc",
                                    fontWeight: 500,
                                }}
                            >
                                {dateFrom || "-"}
                            </Box>
                            <Box component="span" sx={{ mx: 1 }}>
                                -
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    padding: "4px 8px",
                                    background: "#f0f7ff",
                                    borderRadius: "4px",
                                    color: "#0066cc",
                                    fontWeight: 500,
                                }}
                            >
                                {dateTo || "-"}
                            </Box>
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 500,
                                width: "150px",
                                color: "#555",
                                fontSize: "14px",
                            }}
                        >
                            Generat la:
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontSize: "14px",
                                color: "#666",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        display: "inline-block",
                                        width: "8px",
                                        height: "8px",
                                        backgroundColor: "#4caf50",
                                        borderRadius: "50%",
                                    }}
                                />
                                {formatDateTime(generatedTime)}
                            </Box>
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mb: 2,
                }}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadExcel}
                >
                    {t("download_report")}
                </Button>
            </Box>

            <Paper
                elevation={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    borderRadius: 1,
                    boxSizing: "border-box",
                }}
                ref={printRef}
            >
                <TableContainer
                    sx={{
                        maxHeight: "calc(100vh - 290px)",
                        ...scrollbarStyles,
                    }}
                >
                    <Table
                        stickyHeader
                        aria-label="periodic reports table"
                        size="small"
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: "bold",
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 3,
                                        borderBottom:
                                            "1px solid rgba(224, 224, 224, 1)",
                                        bgcolor: "#f5f5f5",
                                        fontSize: "12px",
                                    }}
                                >
                                    Numărul Raportului Z
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: "bold",
                                        minWidth: 120,
                                        borderBottom:
                                            "1px solid rgba(224, 224, 224, 1)",
                                        fontSize: "12px",
                                        bgcolor: "white",
                                    }}
                                >
                                    Data și ora
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: "bold",
                                        minWidth: 100,
                                        borderBottom:
                                            "1px solid rgba(224, 224, 224, 1)",
                                        fontSize: "12px",
                                        bgcolor: "white",
                                    }}
                                >
                                    Numărul bonurilor fiscale
                                </TableCell>
                                {uniqueTaxes.map((tax, index) => {
                                    const labels = getTaxColumnLabels(tax);
                                    return (
                                        <React.Fragment key={`tax-${index}`}>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: "bold",
                                                    minWidth: 150,
                                                    borderBottom:
                                                        "1px solid rgba(224, 224, 224, 1)",
                                                    fontSize: "12px",
                                                    bgcolor: "white",
                                                }}
                                            >
                                                {labels.total}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: "bold",
                                                    minWidth: 150,
                                                    borderBottom:
                                                        "1px solid rgba(224, 224, 224, 1)",
                                                    fontSize: "12px",
                                                    bgcolor: "white",
                                                }}
                                            >
                                                {labels.tva}
                                            </TableCell>
                                        </React.Fragment>
                                    );
                                })}

                                {renderPaymentMethodColumns()}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reports &&
                                reports.map((report, idx) => {
                                    if (!report || !report.soap_data) {
                                        return null;
                                    }

                                    const datetime = report.soap_data.datetime
                                        ? report.soap_data.datetime.split("T")
                                        : ["", ""];

                                    return (
                                        <TableRow
                                            hover
                                            key={report._id || idx}
                                            sx={{
                                                "&:last-child td, &:last-child th":
                                                    {
                                                        border: 0,
                                                    },
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    position: "sticky",
                                                    left: 0,
                                                    bgcolor: "background.paper",
                                                    zIndex: 1,
                                                    padding: "6px 16px",
                                                    borderBottom:
                                                        "1px solid rgba(224, 224, 224, 0.5)",
                                                }}
                                            >
                                                {report.soap_data.report || "-"}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    padding: "6px 16px",
                                                    borderBottom:
                                                        "1px solid rgba(224, 224, 224, 0.5)",
                                                }}
                                            >
                                                <div>
                                                    {formatDate(datetime[0])}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: "0.75rem",
                                                        color: "rgba(0, 0, 0, 0.6)",
                                                    }}
                                                >
                                                    {formatTime(datetime[1])}
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    padding: "6px 16px",
                                                    borderBottom:
                                                        "1px solid rgba(224, 224, 224, 0.5)",
                                                }}
                                            >
                                                {report.soap_data.daily
                                                    ?.receipts?.field?.[0]
                                                    ?.count || "0"}
                                            </TableCell>

                                            {uniqueTaxes.map((tax, index) => {
                                                const values = getTaxValues(
                                                    report,
                                                    tax
                                                );
                                                return (
                                                    <React.Fragment
                                                        key={`tax-value-${index}`}
                                                    >
                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                padding:
                                                                    "6px 16px",
                                                                borderBottom:
                                                                    "1px solid rgba(224, 224, 224, 0.5)",
                                                            }}
                                                        >
                                                            {formatCurrency(
                                                                values.total
                                                            )}
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                padding:
                                                                    "6px 16px",
                                                                borderBottom:
                                                                    "1px solid rgba(224, 224, 224, 0.5)",
                                                            }}
                                                        >
                                                            {formatCurrency(
                                                                values.tva
                                                            )}
                                                        </TableCell>
                                                    </React.Fragment>
                                                );
                                            })}

                                            {renderPaymentValueCells(report)}
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                        <TableFooter>
                            <TableRow
                                sx={{
                                    bgcolor: "#f5f5f5",
                                    "& td": {
                                        fontWeight: "bold",
                                        borderTop:
                                            "2px solid rgba(224, 224, 224, 1)",
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        position: "sticky",
                                        left: 0,
                                        bgcolor: "#f5f5f5",
                                        zIndex: 1,
                                        padding: "8px 16px",
                                        fontSize: "0.8rem",
                                        textAlign: "center",
                                    }}
                                >
                                    Total
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{ padding: "8px 16px" }}
                                ></TableCell>
                                <TableCell
                                    align="center"
                                    sx={{ padding: "8px 16px" }}
                                >
                                    {totals?.receipts || 0}
                                </TableCell>

                                {uniqueTaxes.map((tax, index) => {
                                    const key = `${tax.code}-${tax.percent}`;
                                    return (
                                        <React.Fragment
                                            key={`tax-total-${index}`}
                                        >
                                            <TableCell
                                                align="center"
                                                sx={{ padding: "8px 16px" }}
                                            >
                                                {formatCurrency(
                                                    totals?.[`${key}-total`] ||
                                                        "0"
                                                )}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{ padding: "8px 16px" }}
                                            >
                                                {formatCurrency(
                                                    totals?.[`${key}-tva`] ||
                                                        "0"
                                                )}
                                            </TableCell>
                                        </React.Fragment>
                                    );
                                })}

                                {renderPaymentTotalCells()}
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default GestionarReports;

const scrollbarStyles = {
    overflowX: "scroll",
    overflowY: "scroll",
    "&::-webkit-scrollbar": {
        height: "8px",
        width: "8px",
    },
    "&::-webkit-scrollbar-track": {
        backgroundColor: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#c1c1c1",
        borderRadius: "4px",
        "&:hover": {
            backgroundColor: "#a8a8a8",
        },
    },
    scrollbarWidth: "thin",
    scrollbarColor: "#c1c1c1 #f1f1f1",
    msOverflowStyle: "auto",
};
